import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'

export type ZoomReconnectionModalProps = {
  /**
   * OAuth認証を開始する関数
   */
  onConnectOAuth: () => boolean
  /**
   * ポップアップブロックを検知したときに発火させる関数
   */
  onPopupBlock: () => void
  /**
   * 再接続モーダルを表示してからのタイムアウト時間
   */
  timeout?: number
}
const useZoomReconnectionModal = ({
  onConnectOAuth,
  onPopupBlock,
  timeout = 1000 * 60 * 3,
}: ZoomReconnectionModalProps): {
  /**
   * 再接続を開始される時に発火させると再接続を促すモーダルを開く
   */
  zoomReconnectionStart: () => void
  /**
   * 再接続が成功した時に発火させると成功モーダルを開く
   */
  zoomReconnectionSuccess: () => void
  /**
   * この変数をjsx内に配置するとモーダルが表示される
   */
  zoomReconnectionModalComponent: JSX.Element | null
} => {
  const { t } = useTranslation()
  const [zoomConnectionStatus, setZoomConnectionStatus] = useState<
    'connecting' | 'success' | null
  >(null)

  useEffect(() => {
    // zoom再接続クリック後timeoutを過ぎてもzoomConnectionStatusが変わらない場合はモーダルを閉じる
    if (zoomConnectionStatus === 'connecting') {
      const timer = setTimeout(() => {
        setZoomConnectionStatus(null)
      }, timeout)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomConnectionStatus])

  const zoomReconnectionModal = (
    <Modal
      open={zoomConnectionStatus === 'connecting'}
      handleClose={() => {
        // タイムアウトまで閉じることはできない仕様
      }}
    >
      <ConfirmContent
        onSubmit={() => {
          let opened = false
          opened = onConnectOAuth()
          if (!opened) {
            onPopupBlock()
            setZoomConnectionStatus(null)
          }
        }}
        submitLabel={t('再接続')}
        submitVariant="contained"
        title={t(
          'ZoomアカウントとYaDocQuickの接続の有効期限が切れています。再接続ボタンを押して、Zoomアカウントを再接続してください。'
        )}
      />
    </Modal>
  )

  const zoomReconnectedModal = (
    <Modal
      open={zoomConnectionStatus === 'success'}
      handleClose={() => setZoomConnectionStatus(null)}
    >
      <ConfirmContent
        onSubmit={() => setZoomConnectionStatus(null)}
        submitLabel={t('OK')}
        title={t(
          'Zoomとの再接続が完了しました。ビデオ通話ボタンから診療を開始してください。'
        )}
      />
    </Modal>
  )
  const zoomReconnectionModalComponent = (
    <>
      {zoomReconnectionModal}
      {zoomReconnectedModal}
    </>
  )

  return {
    zoomReconnectionStart: () => setZoomConnectionStatus('connecting'),
    zoomReconnectionSuccess: () => setZoomConnectionStatus('success'),
    zoomReconnectionModalComponent,
  }
}

export default useZoomReconnectionModal
